import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Card,
  CardContent,
  Container,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Loader from 'src/components/loader/loader';
import HttpServices from 'src/services/httpService';
import { userModuleURL } from 'src/services/urlService';
import { CommonHelper } from 'src/utils/commonHelper';
import { LocalStorage } from 'src/utils/storage/local-storage';
import { useToaster } from 'src/utils/toaster/toasterContext';
import ValidationTool from 'src/utils/validationHelper';
import useResponsive from '../hooks/useResponsive';
import GoogleSignInComponent from './google-signin/googleSignIn.component';
import StarIcon from '@mui/icons-material/Star';
import ForumIcon from '@mui/icons-material/Forum';
import QuizIcon from '@mui/icons-material/Quiz';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';

const StyledRoot = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    display: 'flex',
  },
}));

const Register = () => {
  const { t: translate } = useTranslation();
  const theme = useTheme();
  const mdUp = useResponsive('up', 'md');
  const smDown = useResponsive('down', 'sm');
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isCustomDomain = CommonHelper.CustomDomain();

  //State Variable

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const appLogo = CommonHelper.GetAppLogo(true);
  const [formSchema, setFormSchema] = useState({});
  const [formValidationErrors, setFormValidationErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [loader, setLoader] = useState(false);
  const { showToast } = useToaster();
  const [loading, setLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const [timeZone, setTimeZone] = useState('UTC');
  const featureHighlights = [
    {
      title: translate('register_page.features.live_chat_inbox.title', { defaultValue: 'Live chat inbox' }),
      description: translate('register_page.features.live_chat_inbox.description', { defaultValue: 'See every visitor conversation in one place and reply with your team in real time.' }),
      icon: ForumIcon,
    },
    {
      title: translate('register_page.features.ai_chat_assistant.title', { defaultValue: 'AI chat assistant' }),
      description: translate('register_page.features.ai_chat_assistant.description', { defaultValue: 'Handle first-response support instantly and pass complex chats to a human agent.' }),
      icon: SmartToyIcon,
    },
    {
      title: translate('register_page.features.faq_help_flows.title', { defaultValue: 'FAQ and help flows' }),
      description: translate('register_page.features.faq_help_flows.description', { defaultValue: 'Publish reusable answers and guided self-service content directly inside the widget.' }),
      icon: QuizIcon,
    },
    {
      title: translate('register_page.features.tickets.title', { defaultValue: 'Tickets' }),
      description: translate('register_page.features.tickets.description', {
        defaultValue: 'Convert missed chats and email replies into trackable support tickets.',
      }),
      icon: ConfirmationNumberIcon,
    },
    {
      title: translate('register_page.features.shared_agent_coverage.title', { defaultValue: 'Shared agent coverage' }),
      description: translate('register_page.features.shared_agent_coverage.description', { defaultValue: 'Let multiple agents join, respond, and close conversations from one inbox.' }),
      icon: SupportAgentIcon,
    },
    {
      title: translate('register_page.features.smart_notifications.title', { defaultValue: 'Smart notifications' }),
      description: translate('register_page.features.smart_notifications.description', { defaultValue: 'Trigger browser and email alerts when a new visitor starts or updates a chat.' }),
      icon: NotificationsActiveIcon,
    },
  ];
  const gettingStartedSteps = [
    translate('register_page.steps.create_account.title', { defaultValue: 'Create your account' }),
    translate('register_page.steps.install_widget.title', { defaultValue: 'Install the chat widget' }),
    translate('register_page.steps.manage_live_chats.title', { defaultValue: 'Manage live chats and AI replies' }),
  ];
  useEffect(() => {
    const localTimeZone = CommonHelper.GetLocalTimeZone();
    if (localTimeZone) {
      setTimeZone(localTimeZone);
    }
    let token = LocalStorage.getItem('token');
    if (token) {
      navigate('/new-post');
    } else {
      generateFormModelSchema();
    }
  }, []);

  const generateFormModelSchema = () => {
    let fields = [
      ValidationTool.GenerateSchema('firstName', translate('profile.general.first_name'), 'TEXT', true),
      ValidationTool.GenerateSchema('email', translate('profile.general.email'), 'EMAIL', true),
      ValidationTool.GenerateSchema('password', translate('profile.general.password'), 'TEXT', true),
    ];
    const schema = ValidationTool.GenerateSchemaModel(fields, translate);
    setFormSchema(schema);
  };

  const handleInputChange = (event) => {
    let value = event.target.value;
    let name = event.target.name;
    switch (name) {
      case 'email':
        setEmail(value);
        break;
      case 'password':
        setPassword(value);
        break;
      case 'firstName':
        setFirstName(value);
        break;
      case 'lastName':
        setLastName(value);
        break;
      default:
        break;
    }
  };

  const handleOnBlur = (event) => {
    let fieldName = event.target.name;
    let fieldValue = event.target.value;
    let validationErrors = { ...formValidationErrors };
    let message = ValidationTool.ValidateInput(formSchema, fieldValue, fieldName);
    if (message) validationErrors[fieldName] = message;
    else validationErrors[fieldName] = '';
    setFormValidationErrors(validationErrors);
  };

  const handleRegister = () => {
    let registerForm = {
      firstName: firstName,
      email: email,
      password: password,
      device_type: isMobile ? CommonHelper.DeviceType.Mobile : CommonHelper.DeviceType.Desktop,
    };
    let { formValidationErrors, isValidForm } = ValidationTool.FormValidation(formSchema, registerForm);
    if (isValidForm) handleCreateNewUser();
    else setFormValidationErrors(formValidationErrors);
  };

  const handleLogin = () => {
    let loginForm = {
      email: 'demo@brand2chat.ai',
      password: 'demo1234',
    };
    let postProps = {
      url: userModuleURL.login,
      body: loginForm,
      successCallback: loginSuccessCallback,
      failureCallback,
    };
    HttpServices.Post(postProps);
  };

  const handleCreateNewUser = () => {
    setLoading(true);
    let registerForm = {
      first_name: firstName,
      last_name: lastName,
      email: email,
      password: password,
      timezone: timeZone,
      is_custom_domain :isCustomDomain,
    };
    let postProps = {
      url: userModuleURL.register,
      body: registerForm,
      successCallback,
      failureCallback,
    };
    HttpServices.Post(postProps);
  };
  const loginSuccessCallback = (data, message) => {
    message && showToast(message);
    CommonHelper.AppLoginCall(data);
  };
  const successCallback = (data, message) => {
    message && showToast(message);
    data['is_register'] = true;
    CommonHelper.AppLoginCall(data);
  };
  const failureCallback = (message) => {
    message && showToast(message, 'e');
    setLoading(false);
  };
  const navigateToLogin = () => {
    navigate('/login');
  };
  const applyDemoCredential = () => {
    setDemoLoading(true);
    handleLogin();
  };

  return (
    <>
      {loader && <Loader />}
      <Helmet>
        <title> {translate('common.register')} </title>
      </Helmet>
      <StyledRoot>
        {mdUp && (
          <Box
            sx={{
              flex: 1,
              minHeight: '100vh',
              background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 60%, #fff 100%)`,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              position: 'relative',
              px: 6,
              py: 4,
              boxShadow: '0 8px 32px rgba(0,0,0,0.10)',
              borderTopRightRadius: 32,
              borderBottomRightRadius: 32,
            }}
          >
            <img
              src={appLogo}
              style={{ height: 56, marginBottom: 36, filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.10))' }}
              alt="logo"
            />
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                mb: 2,
                letterSpacing: 1,
                textShadow: '0 2px 8px rgba(0,0,0,0.10)',
                textAlign: 'center',
              }}
            >
              {translate('register.welcome_to')} {CommonHelper.GetAppName()}
            </Typography>
            {/* Feature Highlights */}
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, mb: 2, textAlign: 'center', color: '#fff' }}
            >
              {translate('register.feature_highlights')}
            </Typography>
            <Stack direction="row" spacing={3} sx={{ mb: 5, mt: 2, justifyContent: 'center', width: '100%' }}>
              {featureHighlights.map((feature) => {
                const Icon = feature.icon;
                return (
                  <Box key={feature.title} sx={{ textAlign: 'center', px: 2, minWidth: 120}}>
                    <Icon
                      sx={{ fontSize: 44, color: '#fffde7', mb: 1, filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.10))' }}
                    />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.85, fontSize: 15 }}>
                      {feature.description}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
            {/* Getting Started Steps */}
            <Box
              sx={{
                mb: 5,
                width: '100%',
                maxWidth: 380,
                background: 'rgba(255,255,255,0.10)',
                borderRadius: 2,
                p: 2.5,
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 800, mb: 1, color: '#fff', letterSpacing: 0.5, textAlign: 'center' }}
              >
                {translate('register.get_started_steps')}
              </Typography>
              <Stack spacing={1.2}>
                {gettingStartedSteps.map((step, index) => (
                  <Typography key={step} variant="body2" sx={{ color: '#fff', fontWeight: 500, fontSize: 16, textAlign: 'center' }}>
                    <b>{index + 1}.</b> {step}
                  </Typography>
                ))}
              </Stack>
            </Box>
          </Box>
        )}
        <Container maxWidth="sm" sx={{ display: 'flex', alignItems: 'flex-start', height: '100vh', overflowY: 'auto', pt: 2 }}>
          <Box sx={{ width: '100%' }}>
            {smDown ? (
              <Box sx={{ p: 2 }}>
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <img src={appLogo} style={{ height: 32, marginBottom: 16 }} alt="logo" />
                  <Typography variant="h5" fontWeight={700} gutterBottom>
                    {translate('registration.register_in')} {CommonHelper.GetAppName()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {translate('register.welcome_to')} {CommonHelper.GetAppName()}
                  </Typography>
                </Box>
                <Divider sx={{ mb: 3 }} />
                <Stack spacing={3} sx={{ mb: 5 }}>
                  <TextField
                    error={formValidationErrors?.firstName}
                    color={firstName === '' ? 'error' : 'success'}
                    name="firstName"
                    label={translate('profile.general.first_name')}
                    onChange={handleInputChange}
                    value={firstName}
                    helperText={formValidationErrors?.firstName}
                    onBlur={handleOnBlur}
                  />
                  <TextField
                    name="lastName"
                    label={translate('profile.general.last_name')}
                    onChange={handleInputChange}
                    value={lastName}
                  />
                  <TextField
                    error={formValidationErrors?.email}
                    color={email === '' ? 'error' : 'success'}
                    name="email"
                    label={translate('profile.general.email')}
                    onChange={handleInputChange}
                    value={email}
                    helperText={formValidationErrors?.email}
                    onBlur={handleOnBlur}
                  />
                  <TextField
                    error={formValidationErrors?.password}
                    color={password === '' ? 'error' : 'success'}
                    name="password"
                    label={translate('profile.general.password')}
                    type={showPassword ? 'text' : 'password'}
                    onChange={handleInputChange}
                    onBlur={handleOnBlur}
                    value={password}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                            {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    helperText={formValidationErrors?.password}
                  />
                </Stack>
                <LoadingButton
                  fullWidth
                  size="large"
                  type="submit"
                  loading={loading}
                  variant="contained"
                  onClick={handleRegister}
                >
                  {translate('common.register')}
                </LoadingButton>
                {!isCustomDomain && (
                  <>
                    <Stack
                      style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '20px' }}
                    >
                      <LoadingButton
                        fullWidth
                        size="large"
                        loading={demoLoading}
                        variant="outlined"
                        color="info"
                        startIcon={<StarIcon />}
                        onClick={applyDemoCredential}
                        sx={{
                          borderWidth: 2,
                          borderColor: theme.palette.info.main,
                          fontWeight: 'bold',
                          letterSpacing: 1,
                          backgroundColor: '#e3f2fd',
                          color: theme.palette.info.dark,
                          '&:hover': {
                            backgroundColor: '#bbdefb',
                          },
                        }}
                      >
                        {translate('register.use_demo_account')}
                      </LoadingButton>
                    </Stack>
                    <Typography
                      variant="caption"
                      align="center"
                      display="block"
                      sx={{ mt: 1, color: 'info.main', fontWeight: 500 }}
                    >
                      {translate('register.try_demo_account')}
                    </Typography>
                  </>
                )}
                {!isCustomDomain &&<Stack style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '20px' }}>
                  <GoogleSignInComponent
                    handleLoginSuccess={successCallback}
                    googleButtonText={translate('register.register_via_google')}
                  />
                </Stack>}
                <Divider sx={{ my: 3 }} />
                <Typography variant="body2" align="center">
                  {translate('register.already_have_an_account')}?{' '}
                  <Link
                    onClick={navigateToLogin}
                    sx={{
                      color: 'primary.main',
                      fontWeight: 'bold',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                    }}
                  >
                    {translate('register.login_here')}
                  </Link>
                </Typography>
              </Box>
            ) : (
              <Card elevation={8} sx={{ borderRadius: 4, p: { xs: 2, sm: 4 } }}>
                <CardContent>
                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <img src={appLogo} style={{ height: 40, marginBottom: 16 }} alt="logo" />
                    <Typography variant="h4" fontWeight={700} gutterBottom>
                      {translate('registration.register_in')} {CommonHelper.GetAppName()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {translate('register.welcome_to')} {CommonHelper.GetAppName()}
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 3 }} />
                  <Stack spacing={3} sx={{ mb: 5 }}>
                    <TextField
                      error={formValidationErrors?.firstName}
                      color={firstName === '' ? 'error' : 'success'}
                      name="firstName"
                      label={translate('profile.general.first_name')}
                      onChange={handleInputChange}
                      value={firstName}
                      helperText={formValidationErrors?.firstName}
                      onBlur={handleOnBlur}
                    />
                    <TextField
                      name="lastName"
                      label={translate('profile.general.last_name')}
                      onChange={handleInputChange}
                      value={lastName}
                    />
                    <TextField
                      error={formValidationErrors?.email}
                      color={email === '' ? 'error' : 'success'}
                      name="email"
                      label={translate('profile.general.email')}
                      onChange={handleInputChange}
                      value={email}
                      helperText={formValidationErrors?.email}
                      onBlur={handleOnBlur}
                    />
                    <TextField
                      error={formValidationErrors?.password}
                      color={password === '' ? 'error' : 'success'}
                      name="password"
                      label={translate('profile.general.password')}
                      type={showPassword ? 'text' : 'password'}
                      onChange={handleInputChange}
                      onBlur={handleOnBlur}
                      value={password}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                              {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      helperText={formValidationErrors?.password}
                    />
                  </Stack>
                  <LoadingButton
                    fullWidth
                    size="large"
                    type="submit"
                    loading={loading}
                    variant="contained"
                    onClick={handleRegister}
                  >
                    {translate('common.register')}
                  </LoadingButton>
                  {!isCustomDomain &&(
                    <>
                      <Stack
                        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '20px' }}
                      >
                        <LoadingButton
                          fullWidth
                          size="large"
                          loading={demoLoading}
                          variant="outlined"
                          color="info"
                          startIcon={<StarIcon />}
                          onClick={applyDemoCredential}
                          sx={{
                            borderWidth: 2,
                            borderColor: theme.palette.info.main,
                            fontWeight: 'bold',
                            letterSpacing: 1,
                            backgroundColor: '#e3f2fd',
                            color: theme.palette.info.dark,
                            '&:hover': {
                              backgroundColor: '#bbdefb',
                            },
                          }}
                        >
                          {translate('register.use_demo_account')}
                        </LoadingButton>
                      </Stack>
                      <Typography
                        variant="caption"
                        align="center"
                        display="block"
                        sx={{ mt: 1, color: 'info.main', fontWeight: 500 }}
                      >
                        {translate('register.try_demo_account')}
                      </Typography>
                    </>
                  )}
                 {!isCustomDomain && <Stack style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '20px' }}>
                    <GoogleSignInComponent
                      handleLoginSuccess={successCallback}
                      googleButtonText={translate('register.register_via_google')}
                    />
                  </Stack>}
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="body2" align="center">
                    {translate('register.already_have_an_account')}?{' '}
                    <Link
                      onClick={navigateToLogin}
                      sx={{
                        color: 'primary.main',
                        fontWeight: 'bold',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                      }}
                    >
                      {translate('register.login_here')}
                    </Link>
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Box>
        </Container>
      </StyledRoot>
    </>
  );
};
export default Register;
